import Pusher from 'pusher-js'
import verConfig from './verConfig'
import { cookie } from './tools'

export const LOTTERY_CHANNEL = 'lottery'
export const LOTTERY_OPENING_EVENT = 'lottery-opening'
export const LOTTERY_RESULT_EVENT = 'lottery-result'
export const LOTTERY_ORDER_SETTLED_EVENT = 'lottery-order-settled'
export const LOTTERY_SOCKET_STATUS_EVENT = 'lottery-socket-status'

const PUSHER_OPENING_EVENT = 'lottery.opening'
const PUSHER_RESULT_EVENT = 'lottery.result'
const PUSHER_ORDER_SETTLED_EVENT = 'lottery.order.settled'
const receivedResults = Object.create(null)
const receivedResultKeys = []
const receivedSettlements = Object.create(null)
const receivedSettlementKeys = []
const MAX_RECEIVED_RESULTS = 1000

let pusher = null
let lotteryChannel = null
let userChannel = null
let userChannelName = ''
let currentAuthToken = ''

function createWindowEvent(name, detail) {
  if (typeof window === 'undefined') return null
  if (typeof window.CustomEvent === 'function') {
    return new window.CustomEvent(name, { detail })
  }

  const event = document.createEvent('CustomEvent')
  event.initCustomEvent(name, false, false, detail)
  return event
}

function dispatchWindowEvent(name, detail) {
  const event = createWindowEvent(name, detail)
  if (event) window.dispatchEvent(event)
}

function getAuthEndpoint(config) {
  if (config.authEndpoint) return config.authEndpoint
  const apiHost = String(verConfig.url || '').replace(/\/+$/, '')
  return apiHost ? `${apiHost}/api/users/auth` : '/api/users/auth'
}

function parsePayload(payload) {
  if (typeof payload !== 'string') return payload
  try {
    return JSON.parse(payload)
  } catch (error) {
    return null
  }
}

function normalizeLotteryOpening(payload) {
  const opening = parsePayload(payload)
  if (!opening || typeof opening !== 'object') return null

  const endtime = Number(opening.endtime)
  const serverTime = Number(opening.server_time)
  if (
    !opening.type ||
    opening.expect === undefined ||
    opening.expect === null ||
    String(opening.expect) === '' ||
    !Number.isFinite(endtime) ||
    !Number.isFinite(serverTime)
  ) {
    return null
  }

  return {
    ...opening,
    type: String(opening.type),
    expect: String(opening.expect),
    endtime,
    opentime: Number(opening.opentime) || 0,
    server_time: serverTime,
    countdown: Math.max(0, Math.floor(endtime - serverTime)),
  }
}

function normalizeLotteryResult(payload) {
  const result = parsePayload(payload)
  if (
    !result ||
    typeof result !== 'object' ||
    !result.type ||
    result.expect === undefined ||
    result.expect === null ||
    String(result.expect) === '' ||
    result.code === undefined
  ) {
    return null
  }

  return {
    ...result,
    type: String(result.type),
    expect: String(result.expect),
  }
}

function normalizeOrderSettlement(payload) {
  const settlement = parsePayload(payload)
  if (
    !settlement ||
    typeof settlement !== 'object' ||
    !settlement.type ||
    settlement.expect === undefined ||
    settlement.expect === null ||
    String(settlement.expect) === '' ||
    !Array.isArray(settlement.orders)
  ) {
    return null
  }

  const balance = Number(settlement.balance)
  return {
    ...settlement,
    type: String(settlement.type),
    expect: String(settlement.expect),
    balance: Number.isFinite(balance) ? balance : settlement.balance,
  }
}

function handleLotteryOpening(payload) {
  const opening = normalizeLotteryOpening(payload)
  if (!opening) return
  dispatchWindowEvent(LOTTERY_OPENING_EVENT, opening)
}

function rememberResult(key) {
  receivedResults[key] = true
  receivedResultKeys.push(key)
  if (receivedResultKeys.length <= MAX_RECEIVED_RESULTS) return
  const expiredKey = receivedResultKeys.shift()
  delete receivedResults[expiredKey]
}

function handleLotteryResult(payload) {
  const result = normalizeLotteryResult(payload)
  if (!result) return

  const key = `${result.type}:${result.expect}`
  if (receivedResults[key]) return
  rememberResult(key)
  dispatchWindowEvent(LOTTERY_RESULT_EVENT, result)
}

function updateStoredUserBalance(balance) {
  if (!Number.isFinite(Number(balance))) return
  try {
    const storedUser = JSON.parse(cookie.get('userInfo') || '{}')
    if (!storedUser || typeof storedUser !== 'object') return
    cookie.set('userInfo', JSON.stringify({ ...storedUser, money: Number(balance) }))
  } catch (error) {
    console.warn('[lottery-wss] 更新本地余额失败', error)
  }
}

function handleLotteryOrderSettled(payload) {
  const settlement = normalizeOrderSettlement(payload)
  if (!settlement) return

  const key = `${userChannelName}:${settlement.type}:${settlement.expect}`
  if (receivedSettlements[key]) return
  receivedSettlements[key] = true
  receivedSettlementKeys.push(key)
  if (receivedSettlementKeys.length > MAX_RECEIVED_RESULTS) {
    const expiredKey = receivedSettlementKeys.shift()
    delete receivedSettlements[expiredKey]
  }

  updateStoredUserBalance(settlement.balance)
  dispatchWindowEvent(LOTTERY_ORDER_SETTLED_EVENT, settlement)
}

function bindConnectionEvents(instance) {
  instance.connection.bind('state_change', states => {
    if (states.current === 'failed' || states.current === 'unavailable') {
      console.error('[lottery-wss] 长连接状态异常', states)
      return
    }
  })
  instance.connection.bind('connected', () => {
    dispatchWindowEvent(LOTTERY_SOCKET_STATUS_EVENT, { status: 'connected' })
  })
  instance.connection.bind('disconnected', () => {
    console.warn('[lottery-wss] 长连接已断开，正在等待自动重连')
    dispatchWindowEvent(LOTTERY_SOCKET_STATUS_EVENT, { status: 'disconnected' })
  })
  instance.connection.bind('error', error => {
    console.error('[lottery-wss] 长连接失败', error)
    dispatchWindowEvent(LOTTERY_SOCKET_STATUS_EVENT, { status: 'error', error })
  })
}

function bindChannelEvents(channel) {
  channel.bind('pusher:subscription_succeeded', () => {
  })
  channel.bind('pusher:subscription_error', error => {
    console.error('[lottery-wss] lottery 频道订阅失败', error)
  })
}

/**
 * 建立全站唯一的彩票 Pusher 连接。
 * 客户端只使用 appKey；appSecret 严禁写入前端。
 */
export function connectLotterySocket(options = {}) {
  if (pusher) return lotteryChannel

  const config = { ...(verConfig.wss || {}), ...options }
  if (!config.appKey) {
    console.warn('[lottery-wss] appKey is required')
    return null
  }

  const pageUsesTLS = typeof window === 'undefined' || window.location.protocol === 'https:'
  const useTLS = typeof config.forceTLS === 'boolean' ? config.forceTLS : pageUsesTLS
  const host = config.host || (typeof window !== 'undefined' ? window.location.hostname : '')
  const port = useTLS ? (Number(config.wssPort) || 443) : (Number(config.wsPort) || 6001)

  try {
    pusher = new Pusher(config.appKey, {
      cluster: config.cluster || 'mt1',
      wsHost: host,
      wsPort: Number(config.wsPort) || 6001,
      wssPort: Number(config.wssPort) || 443,
      forceTLS: useTLS,
      // Pusher 的传输名称固定为 ws；forceTLS=true 时实际建立的是 wss:// 连接。
      enabledTransports: ['ws'],
      enableStats: false,
      channelAuthorization: {
        endpoint: getAuthEndpoint(config),
        transport: 'ajax',
        headersProvider: () => ({
          token: currentAuthToken || cookie.get('token') || '',
        }),
      },
    })
    bindConnectionEvents(pusher)
    lotteryChannel = pusher.subscribe(LOTTERY_CHANNEL)
    bindChannelEvents(lotteryChannel)
    lotteryChannel.bind(PUSHER_OPENING_EVENT, handleLotteryOpening)
    lotteryChannel.bind(PUSHER_RESULT_EVENT, handleLotteryResult)
  } catch (error) {
    pusher = null
    lotteryChannel = null
    dispatchWindowEvent(LOTTERY_SOCKET_STATUS_EVENT, { status: 'error', error })
  }

  return lotteryChannel
}

export function subscribeLotteryUser(userId, token = '') {
  const normalizedUserId = String(userId || '').trim()
  const nextAuthToken = token || cookie.get('token') || ''
  if (!normalizedUserId || !nextAuthToken) return null

  if (!pusher) connectLotterySocket()
  if (!pusher) return null

  const nextChannelName = `private-user-${normalizedUserId}`
  if (userChannel && userChannelName === nextChannelName) {
    currentAuthToken = nextAuthToken
    return userChannel
  }
  unsubscribeLotteryUser()

  currentAuthToken = nextAuthToken
  userChannelName = nextChannelName
  userChannel = pusher.subscribe(userChannelName)
  userChannel.bind('pusher:subscription_succeeded', () => {
  })
  userChannel.bind('pusher:subscription_error', error => {
    console.error('[lottery-wss] 用户订单频道订阅失败', {
      channel: userChannelName,
      error,
    })
  })
  userChannel.bind(PUSHER_ORDER_SETTLED_EVENT, handleLotteryOrderSettled)
  return userChannel
}

export function subscribeStoredLotteryUser() {
  try {
    const storedUser = JSON.parse(cookie.get('userInfo') || '{}')
    return subscribeLotteryUser(storedUser.id, cookie.get('token') || '')
  } catch (error) {
    return null
  }
}

export function unsubscribeLotteryUser() {
  if (!pusher || !userChannelName) return
  if (userChannel) {
    userChannel.unbind(PUSHER_ORDER_SETTLED_EVENT, handleLotteryOrderSettled)
  }
  pusher.unsubscribe(userChannelName)
  userChannel = null
  userChannelName = ''
  currentAuthToken = ''
}

export function disconnectLotterySocket() {
  if (!pusher) return
  unsubscribeLotteryUser()
  if (lotteryChannel) {
    lotteryChannel.unbind(PUSHER_OPENING_EVENT, handleLotteryOpening)
    lotteryChannel.unbind(PUSHER_RESULT_EVENT, handleLotteryResult)
  }
  pusher.unsubscribe(LOTTERY_CHANNEL)
  pusher.disconnect()
  lotteryChannel = null
  pusher = null
}

export function getLotterySocket() {
  return pusher
}

export default {
  connect: connectLotterySocket,
  disconnect: disconnectLotterySocket,
  getInstance: getLotterySocket,
  subscribeUser: subscribeLotteryUser,
  unsubscribeUser: unsubscribeLotteryUser,
}
